import { useEffect, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';

/**
 * Inline-editable link title. Renders the title (or a fallback like the
 * shortCode) with a pencil button next to it; clicking the pencil swaps
 * the title for a text input. Saves on Enter or blur, cancels on Escape.
 *
 * Used in two places:
 *   - LinkDetail page header (single big title row)
 *   - Dashboard links list (each row's title cell)
 *
 * Both surfaces sit inside React Router <Link>s, so every interactive
 * element here stops propagation on mousedown + click — otherwise the
 * pencil tap or input click would also fire row navigation.
 *
 * The title is a purely internal label — never shown to visitors of the
 * actual short link. The pencil's tooltip + the input's placeholder make
 * that explicit.
 */
interface InlineEditableTitleProps {
  /** Current title (null/empty → show fallback). */
  value: string | null | undefined;
  /** Shown in place of an empty title (typically the link's shortCode). */
  fallback: string;
  /** Committed by Enter or blur. Receives `null` for cleared/blank input. */
  onSave: (next: string | null) => Promise<void> | void;
  /** Tailwind classes applied to the visible text + the editing input so
   *  the two states look identical (no layout jump). */
  textClassName?: string;
  /** Pencil icon px. Defaults to 14 (good for list rows); bump to 18+ for
   *  big H1-style titles. */
  iconSize?: number;
}

export function InlineEditableTitle({
  value,
  fallback,
  onSave,
  textClassName = '',
  iconSize = 14,
}: InlineEditableTitleProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep the draft in sync with prop changes when we're NOT mid-edit
  // (e.g. parent refetched the link after another action).
  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  async function commit(next: string) {
    const trimmed = next.trim();
    const nextOrNull = trimmed === '' ? null : trimmed;
    if ((nextOrNull ?? '') === (value ?? '')) {
      // No change — just exit edit mode silently.
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await onSave(nextOrNull);
    } finally {
      setBusy(false);
      setEditing(false);
    }
  }

  function cancel() {
    setDraft(value ?? '');
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        autoFocus
        type="text"
        value={draft}
        disabled={busy}
        placeholder="Internal name — only you see this"
        maxLength={120}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(draft);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onBlur={() => commit(draft)}
        className={`min-w-0 w-full bg-transparent outline-none border-b border-[var(--color-primary)] ${textClassName}`}
      />
    );
  }

  return (
    <span className="group inline-flex min-w-0 items-baseline gap-2">
      <span className={`truncate ${textClassName}`}>{value || fallback}</span>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setEditing(true);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label="Edit internal name"
        title="Internal name — only you see this. Click to edit."
        className="shrink-0 rounded p-1 text-[var(--color-on-surface-variant)] opacity-40 transition hover:bg-[var(--surface-glass)] hover:opacity-100 group-hover:opacity-80"
      >
        <Pencil size={iconSize} />
      </button>
    </span>
  );
}
